import Constants from "utils/Constants";
import { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { Button, Row, Col } from "reactstrap";
import _ from "underscore";
import getSymbolFromCurrency from "currency-symbol-map";
import NumberUtils from "utils/NumberUtils";
import CurrencyInterval from "../Inputs/CurrencyInterval";
import { Tooltip, Whisper, Popover } from "rsuite";
import DisplayRecord from "./DisplayRecord";
import AppSpinner from "../../elements/AppSpinner";
import EmptyField from "./EmptyField";

const CurrencyIntervalRecord = (
  {
    mode,
    field,
    existingValue,
    recordID,
    instanceID,
    tableView,
    modifyTrigger,
    inputSize,
    onUpdate,
    submitting,
    error,
  },
  ref
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [currencyCode, setCurrencyCode] = useState(null);
  const [currencyInterval, setCurrencyInterval] = useState(null);
  const [value, setValue] = useState(null);
  const whisperRef = useRef();

  const startEditing = () => {
    setIsEditing(true);
    setValue(existingValue?.Value);
    setCurrencyCode(existingValue?.Currency || "USD");
    setCurrencyInterval(existingValue?.Interval || "mo");
    whisperRef?.current?.open();
  };

  const save = (e) => {
    e.preventDefault();
    if (value && isNaN(value)) {
      return setLocalError("The value for this field must be numeric.");
    }

    let val = null;

    if (value !== "") {
      val = {
        Value: value,
        Currency: currencyCode,
        Symbol: getSymbolFromCurrency(currencyCode),
        Interval: currencyInterval,
        CalculatedValues: {
          sec: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.SECOND
          ),
          min: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.MINUTE
          ),
          hr: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.HOUR
          ),
          day: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.DAY
          ),
          wk: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.WEEK
          ),
          mo: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.MONTH
          ),
          qtr: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.QUARTER
          ),
          yr: NumberUtils.currencyIntervalAtoB(
            value,
            currencyInterval,
            Constants.CURRENCY_INTERVALS.YEAR
          ),
        },
      };
    }
    onUpdate({
      field,
      recordID,
      payload: { [field.APIName]: val },
      onSuccess: stopEditing,
    });
  };

  const stopEditing = () => {
    setIsEditing(false);
    setValue(null);
    whisperRef?.current?.close();
  };

  const instanceIDRef = useRef();
  instanceIDRef.current = instanceID;

  const handleChange = (val) => {
    if (!val) {
      setValue(null);
      setCurrencyCode(null);
      setCurrencyInterval(null);

      return;
    }

    setValue(val.Value);
    setCurrencyCode(val.Currency);
    setCurrencyInterval(val.Interval);
  };

  const currencyToReadable = (val) => {
    if (!val || val.Value == null) {
      return <EmptyField table={tableView} />;
    }

    if (!val.Currency) {
      return "(unknown currency)";
    }

    let currency = getSymbolFromCurrency(val.Currency);

    if (!currency) {
      return "(unknown currency)";
    }

    let formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: val.Currency,
    });

    return `${formatter.format(parseFloat(val.Value))} ${
      val.Interval ? " / " + val.Interval + "." : ""
    }`;
  };

  const renderCalculatedValues = (val) => {
    let values = [];

    if (!val) {
      return "";
    }

    for (let i = 0; i < _.keys(val.CalculatedValues).length; i++) {
      let key = _.keys(val.CalculatedValues)[i];
      let c = val.CalculatedValues[key];

      values.push(
        currencyToReadable({
          Value: c,
          Currency: val.Currency,
          Symbol: val.Symbol,
          Interval: key,
        })
      );
    }

    return (
      <div className="px-2 py-1 text-left" style={{ maxWidth: "320px" }}>
        <h5 style={{ color: "white" }}>Estimated Conversions</h5>
        <hr className="my-1"></hr>
        <Row>
          {values.map((v, i) => (
            <Col xs="12" sm="6" className="my-1" key={i}>
              {v}
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="auto"
      trigger={isEditing ? "click" : "none"}
      speaker={
        <Popover className="p-0 m-0" style={{ width: "350px" }}>
          <div className="cursor-pointer p-2 text-dark border-bottom border-superlight">
            <Row className="align-items-center">
              <Col xs="">
                <h5 className="text-dark m-0">{field?.Name}</h5>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();

                    stopEditing();
                  }}
                >
                  Done
                </Button>
              </Col>
            </Row>
          </div>
          <div className="p-2">
            <CurrencyInterval
              existingValue={existingValue ?? ""}
              field={field}
              inputSize={inputSize}
              onChange={handleChange}
            ></CurrencyInterval>
          </div>
          <div className="text-right px-2 pt-0 pb-2">
            <Button
              disabled={(field?.Required && !value) || submitting}
              onClick={save}
              size="sm"
              color="primary"
              className="btn-icon-only"
            >
              {submitting ? (
                <AppSpinner />
              ) : (
                <i
                  className="mdi mdi-check"
                  color="white"
                  style={{ fontSize: "20px" }}
                ></i>
              )}
            </Button>
          </div>
          {(localError || error) && (
            <div className="mb-0 text-left pt-2 border-top border-superlight">
              <p className="m-0 text-danger">
                {error && (
                  <small>Unable to save your changes. Try again.</small>
                )}
                {localError && <small>{localError}</small>}
              </p>
            </div>
          )}
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode}
          tableView={tableView}
          modifyTrigger={modifyTrigger}
          openModify={startEditing}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(any, innerRef) =>
            existingValue && !isEditing ? (
              <div ref={innerRef}>
                <Whisper
                  placement="autoVertical"
                  trigger="hover"
                  enterable
                  // delayOpen={300}
                  speaker={
                    <Tooltip style={{ minWidth: 350 }}>
                      {renderCalculatedValues(existingValue) ?? "Loading"}
                    </Tooltip>
                  }
                >
                  <div className="cursor-pointer">
                    {currencyToReadable(existingValue) ?? "Loading"}
                  </div>
                </Whisper>
              </div>
            ) : (
              <div className="cursor-pointer">
                {currencyToReadable(existingValue) ?? "Loading"}
              </div>
            )
          }
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(CurrencyIntervalRecord);
